/**
 * Social Authentication Service
 * 
 * Unified authentication for multiple providers:
 * - Google: Native Firebase support
 * - Apple: Native Firebase support via OAuthProvider
 * - Kakao: OIDC provider (requires Firebase Auth with Identity Platform)
 * - Naver: OIDC provider (requires Firebase Auth with Identity Platform)
 * - Email/Password: Native Firebase support
 * 
 * Note: Kakao and Naver require:
 * 1. Firebase Authentication upgraded to Identity Platform
 * 2. OIDC provider configured in Firebase Console
 * 3. Developer accounts with Kakao/Naver and OAuth app setup
 * 
 * Platform Support:
 * - Web: Uses popup/redirect OAuth flows via Firebase Web SDK
 * - iOS/Android: Uses native sign-in via @capacitor-firebase/authentication
 *   - Google: signInWithGoogle() uses native Google Sign-In SDK
 *   - Apple: signInWithApple() uses native Sign in with Apple
 *   - Kakao/Naver: Web-based OAuth (OIDC) - not natively supported, falls back to redirect
 */

import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCredential,
  OAuthProvider,
  GoogleAuthProvider,
  UserCredential,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth, googleProvider } from './firebaseConfig';

// ============================================================================
// Types
// ============================================================================

export type SocialProvider = 'google' | 'apple' | 'kakao' | 'naver';

export interface SocialAuthResult {
  user: UserCredential['user'];
  providerId: SocialProvider;
  accessToken?: string;
  idToken?: string;
  additionalInfo?: Record<string, unknown>;
}

export interface ProviderConfig {
  providerId: string;
  scopes?: string[];
  customParameters?: Record<string, string>;
}

// ============================================================================
// Provider Configurations
// ============================================================================

/**
 * Provider IDs for Firebase Authentication
 * - Google/Apple: Built-in provider IDs
 * - Kakao/Naver: OIDC provider IDs (must match Firebase Console configuration)
 */
const PROVIDER_IDS: Record<SocialProvider, string> = {
  google: 'google.com',
  apple: 'apple.com',
  // OIDC provider IDs - configure these in Firebase Console
  // Format: oidc.{provider-name}
  kakao: 'oidc.kakao',
  naver: 'oidc.naver',
};

/**
 * Get provider configuration
 */
const getProviderConfig = (provider: SocialProvider): ProviderConfig => {
  switch (provider) {
    case 'google':
      return {
        providerId: PROVIDER_IDS.google,
        scopes: [
          'https://www.googleapis.com/auth/user.birthday.read',
          'https://www.googleapis.com/auth/user.gender.read',
          'profile',
          'email',
        ],
        customParameters: {
          prompt: 'select_account',
          include_granted_scopes: 'true',
        },
      };
    
    case 'apple':
      return {
        providerId: PROVIDER_IDS.apple,
        scopes: ['email', 'name'],
        customParameters: {
          // Localize Apple auth screen (Korean)
          locale: 'ko',
        },
      };
    
    case 'kakao':
      return {
        providerId: PROVIDER_IDS.kakao,
        scopes: ['openid', 'profile_nickname', 'profile_image'],
        customParameters: {},
      };
    
    case 'naver':
      return {
        providerId: PROVIDER_IDS.naver,
        scopes: ['profile', 'email'],
        customParameters: {},
      };
    
    default:
      throw new Error(`Unsupported provider: ${provider}`);
  }
};

// ============================================================================
// Provider Factory
// ============================================================================

/**
 * Create OAuth provider instance for the specified social provider
 */
const createProvider = (socialProvider: SocialProvider): GoogleAuthProvider | OAuthProvider => {
  const config = getProviderConfig(socialProvider);
  
  // Google uses GoogleAuthProvider (already configured in firebaseConfig)
  if (socialProvider === 'google') {
    return googleProvider;
  }
  
  // Apple, Kakao, Naver use OAuthProvider
  const provider = new OAuthProvider(config.providerId);
  
  // Add scopes
  if (config.scopes) {
    config.scopes.forEach(scope => provider.addScope(scope));
  }
  
  // Set custom parameters
  if (config.customParameters) {
    provider.setCustomParameters(config.customParameters);
  }
  
  return provider;
};

// ============================================================================
// Browser Detection
// ============================================================================

/**
 * Detect if running in Chrome (not Edge)
 * Chrome has issues with popup, so we use redirect for Chrome
 */
const isChrome = (): boolean => {
  return /Chrome/.test(navigator.userAgent) && !/Edg\//.test(navigator.userAgent);
};

/**
 * Detect in-app browsers (KakaoTalk, Instagram, Facebook, etc.)
 * These browsers have storage partitioning which breaks OAuth flows
 */
export const isInAppBrowser = (): boolean => {
  const ua = navigator.userAgent || '';
  return /KAKAOTALK|Instagram|FB_IAB|FBAN|FBAV|Line\/|Twitter|NAVER|MicroMessenger/i.test(ua);
};

// ============================================================================
// Native Platform Sign-In (iOS/Android via Capacitor)
// ============================================================================

/**
 * Sign in with Google using native SDK on iOS/Android
 * Uses @capacitor-firebase/authentication plugin
 * 
 * @returns Promise with auth result
 */
async function signInWithGoogleNative(): Promise<SocialAuthResult> {
  console.log('📱 Using native Google Sign-in via @capacitor-firebase/authentication');
  
  // Step 1: Sign in on the native layer
  const result = await FirebaseAuthentication.signInWithGoogle();
  
  if (!result.user) {
    throw new Error('No user returned from Google Sign-in');
  }
  
  console.log('✅ Native Google sign-in successful:', result.user.email);
  
  // Step 2: Sign in on the web layer using the id token
  // This is REQUIRED because the native plugin only signs in on the native layer,
  // but our Firestore operations use the Firebase Web SDK which needs its own auth state
  if (!result.credential?.idToken) {
    throw new Error('No idToken returned from Google Sign-in - cannot sync with Web SDK');
  }
  
  const webCredential = GoogleAuthProvider.credential(result.credential.idToken);
  const webResult = await signInWithCredential(auth, webCredential);
  
  console.log('✅ Web SDK sign-in successful:', webResult.user.email);
  
  return {
    user: webResult.user,
    providerId: 'google',
    accessToken: result.credential?.accessToken ?? undefined,
    idToken: result.credential?.idToken ?? undefined,
  };
}

/**
 * Sign in with Apple using native SDK on iOS/Android
 * Uses @capacitor-firebase/authentication plugin
 * 
 * Note: On iOS, uses native Sign in with Apple
 * On Android, uses web-based OAuth flow managed by the plugin
 * 
 * @returns Promise with auth result
 */
async function signInWithAppleNative(): Promise<SocialAuthResult> {
  console.log('📱 Using native Apple Sign-in via @capacitor-firebase/authentication');
  
  // Step 1: Sign in on the native layer
  const result = await FirebaseAuthentication.signInWithApple();
  
  if (!result.user) {
    throw new Error('No user returned from Apple Sign-in');
  }
  
  console.log('✅ Native Apple sign-in successful:', result.user.email);
  
  // Step 2: Sign in on the web layer using the id token
  if (!result.credential?.idToken) {
    throw new Error('No idToken returned from Apple Sign-in - cannot sync with Web SDK');
  }
  
  // Apple uses OAuthProvider to create credential
  const appleProvider = new OAuthProvider('apple.com');
  const appleCredential = appleProvider.credential({
    idToken: result.credential.idToken,
    rawNonce: result.credential.nonce,
  });
  const webResult = await signInWithCredential(auth, appleCredential);
  
  console.log('✅ Web SDK sign-in successful:', webResult.user.email);
  
  return {
    user: webResult.user,
    providerId: 'apple',
    accessToken: result.credential?.accessToken ?? undefined,
    idToken: result.credential?.idToken ?? undefined,
  };
}

/**
 * Sign in with a social provider using native SDK on iOS/Android
 * 
 * Supported natively: Google, Apple
 * Falls back to web redirect for: Kakao, Naver (OIDC providers)
 * 
 * @param provider - Social provider to use
 * @returns Promise with auth result, or null if redirect was initiated
 */
async function signInWithSocialNative(provider: SocialProvider): Promise<SocialAuthResult | null> {
  switch (provider) {
    case 'google':
      return signInWithGoogleNative();
    
    case 'apple':
      return signInWithAppleNative();
    
    case 'kakao':
    case 'naver':
      // OIDC providers (Kakao, Naver) don't have native SDK support in @capacitor-firebase/authentication
      // Fall back to web-based redirect flow
      // Note: This opens an in-app browser on native platforms
      console.log(`📱 ${provider} uses OIDC - falling back to web redirect`);
      await signInWithSocialRedirect(provider);
      return null;
    
    default:
      throw new Error(`Unsupported provider for native sign-in: ${provider}`);
  }
}

// ============================================================================
// Sign-In Functions (Web)
// ============================================================================

/**
 * Sign in with a social provider using popup
 * 
 * @param provider - Social provider to use
 * @returns Promise with auth result
 */
export async function signInWithSocialPopup(
  provider: SocialProvider
): Promise<SocialAuthResult> {
  const authProvider = createProvider(provider);
  
  try {
    const result = await signInWithPopup(auth, authProvider);
    
    // Extract tokens from result
    let accessToken: string | undefined;
    let idToken: string | undefined;
    
    if (provider === 'google') {
      const credential = GoogleAuthProvider.credentialFromResult(result);
      accessToken = credential?.accessToken ?? undefined;
      idToken = credential?.idToken ?? undefined;
    } else {
      const credential = OAuthProvider.credentialFromResult(result);
      accessToken = credential?.accessToken ?? undefined;
      idToken = credential?.idToken ?? undefined;
    }
    
    return {
      user: result.user,
      providerId: provider,
      accessToken,
      idToken,
    };
  } catch (error) {
    console.error(`Sign-in with ${provider} failed:`, error);
    throw error;
  }
}

/**
 * Sign in with a social provider using redirect
 * 
 * @param provider - Social provider to use
 */
export async function signInWithSocialRedirect(provider: SocialProvider): Promise<void> {
  const authProvider = createProvider(provider);
  await signInWithRedirect(auth, authProvider);
}

/**
 * Handle redirect result after returning from social provider
 * Call this on page load to handle redirect results
 * 
 * @returns Promise with auth result or null if no redirect occurred
 */
export async function handleSocialRedirectResult(): Promise<SocialAuthResult | null> {
  try {
    const result = await getRedirectResult(auth);
    
    if (!result) {
      return null;
    }
    
    // Determine provider from result
    const providerId = result.providerId;
    let provider: SocialProvider = 'google';
    
    if (providerId === 'google.com') {
      provider = 'google';
    } else if (providerId === 'apple.com') {
      provider = 'apple';
    } else if (providerId?.includes('kakao')) {
      provider = 'kakao';
    } else if (providerId?.includes('naver')) {
      provider = 'naver';
    }
    
    // Extract tokens
    let accessToken: string | undefined;
    let idToken: string | undefined;
    
    if (provider === 'google') {
      const credential = GoogleAuthProvider.credentialFromResult(result);
      accessToken = credential?.accessToken ?? undefined;
      idToken = credential?.idToken ?? undefined;
    } else {
      const credential = OAuthProvider.credentialFromResult(result);
      accessToken = credential?.accessToken ?? undefined;
      idToken = credential?.idToken ?? undefined;
    }
    
    return {
      user: result.user,
      providerId: provider,
      accessToken,
      idToken,
    };
  } catch (error) {
    console.error('Redirect result error:', error);
    throw error;
  }
}

/**
 * Smart sign-in that chooses the best method based on platform and browser
 * 
 * Platform routing:
 * - iOS/Android native: Use native SDK via @capacitor-firebase/authentication
 * - Web (Chrome): Use redirect (avoids popup/iframe timeouts)
 * - Web (Edge/Safari/Firefox): Use popup (better UX)
 * - In-app browsers: Show message to open in external browser
 * 
 * @param provider - Social provider to use
 * @returns Promise with auth result or null (if redirect initiated)
 */
export async function signInWithSocial(
  provider: SocialProvider
): Promise<SocialAuthResult | null> {
  // ========================================================================
  // Native Platform (iOS/Android) - Use native SDK
  // ========================================================================
  if (Capacitor.isNativePlatform()) {
    console.log(`📱 Native platform detected: using native sign-in for ${provider}`);
    return signInWithSocialNative(provider);
  }
  
  // ========================================================================
  // Web Platform - Use popup/redirect
  // ========================================================================
  
  // Check for in-app browser first (web only issue)
  if (isInAppBrowser()) {
    const currentUrl = window.location.href;
    const message = 
      '카카오톡/인스타그램 내 브라우저에서는 소셜 로그인이 제한됩니다.\n\n' +
      '아래 방법으로 외부 브라우저에서 열어주세요:\n\n' +
      '📱 iPhone: 오른쪽 하단 "..." → "Safari에서 열기"\n' +
      '📱 Android: 오른쪽 상단 "⋮" → "다른 브라우저로 열기"\n\n' +
      '또는 링크를 복사하여 Safari/Chrome에서 직접 열어주세요:\n' +
      currentUrl;
    
    // Try to copy URL to clipboard
    try {
      await navigator.clipboard.writeText(currentUrl);
    } catch (e) {
      // Clipboard access may be denied
    }
    
    throw new Error(message);
  }
  
  try {
    // Chrome: prefer redirect to avoid apis.google.com timeout issues
    if (isChrome()) {
      console.log(`Chrome detected: using redirect for ${provider}`);
      await signInWithSocialRedirect(provider);
      return null; // Redirected, result handled on return
    }
    
    // Other browsers: use popup for better UX
    console.log(`Non-Chrome browser: using popup for ${provider}`);
    return await signInWithSocialPopup(provider);
    
  } catch (error) {
    console.error(`Initial ${provider} sign-in attempt failed:`, error);
    
    // Fallback: try redirect if popup fails
    try {
      console.log(`Fallback: trying redirect for ${provider}`);
      await signInWithSocialRedirect(provider);
      return null;
    } catch (redirectError) {
      console.error(`Sign-in with ${provider} failed (both methods):`, redirectError);
      throw error; // Throw original error
    }
  }
}

// ============================================================================
// Provider Display Info
// ============================================================================

export interface ProviderDisplayInfo {
  id: SocialProvider;
  name: string;
  nameKo: string;
  icon: string;
  color: string;
  textColor: string;
}

/**
 * Get display information for social providers
 * Useful for rendering login buttons
 */
export const PROVIDER_DISPLAY: Record<SocialProvider, ProviderDisplayInfo> = {
  google: {
    id: 'google',
    name: 'Google',
    nameKo: '구글',
    icon: '🔵', // Replace with actual icon component
    color: '#ffffff',
    textColor: '#757575',
  },
  apple: {
    id: 'apple',
    name: 'Apple',
    nameKo: '애플',
    icon: '🍎', // Replace with actual icon component
    color: '#000000',
    textColor: '#ffffff',
  },
  kakao: {
    id: 'kakao',
    name: 'Kakao',
    nameKo: '카카오',
    icon: '💬', // Replace with actual icon component
    color: '#FEE500',
    textColor: '#000000',
  },
  naver: {
    id: 'naver',
    name: 'Naver',
    nameKo: '네이버',
    icon: '🟢', // Replace with actual icon component
    color: '#03C75A',
    textColor: '#ffffff',
  },
};

/**
 * Get list of enabled providers
 * Modify this based on which providers you've configured in Firebase Console
 */
export const getEnabledProviders = (): SocialProvider[] => {
  // Currently only Google is fully configured
  // Add 'apple', 'kakao', 'naver' as you configure them in Firebase Console
  return ['google', 'apple', 'kakao', 'naver'];
};

// ============================================================================
// Email/Password Authentication
// ============================================================================

export interface EmailAuthResult {
  user: UserCredential['user'];
  isNewUser: boolean;
}

export interface EmailAuthError {
  code: string;
  message: string;
  field?: 'email' | 'password' | 'general';
}

/**
 * Sign up with email and password
 * Creates a new user account and sends verification email
 */
export async function signUpWithEmail(
  email: string,
  password: string,
  displayName?: string
): Promise<EmailAuthResult> {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update display name if provided
    if (displayName && result.user) {
      await updateProfile(result.user, { displayName });
    }
    
    // Send verification email
    if (result.user) {
      await sendEmailVerification(result.user);
      console.log('📧 Verification email sent to:', email);
    }
    
    return {
      user: result.user,
      isNewUser: true,
    };
  } catch (error: any) {
    console.error('Sign up error:', error.code, error.message);
    throw parseEmailAuthError(error);
  }
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(
  email: string,
  password: string
): Promise<EmailAuthResult> {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    
    return {
      user: result.user,
      isNewUser: false,
    };
  } catch (error: any) {
    console.error('Sign in error:', error.code, error.message);
    throw parseEmailAuthError(error);
  }
}

/**
 * Send password reset email
 */
export async function resetPassword(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email);
    console.log('📧 Password reset email sent to:', email);
  } catch (error: any) {
    console.error('Password reset error:', error.code, error.message);
    throw parseEmailAuthError(error);
  }
}

/**
 * Resend email verification
 */
export async function resendVerificationEmail(): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    throw { code: 'auth/no-user', message: '로그인이 필요합니다.', field: 'general' };
  }
  
  if (user.emailVerified) {
    throw { code: 'auth/already-verified', message: '이미 이메일이 인증되었습니다.', field: 'general' };
  }
  
  try {
    await sendEmailVerification(user);
    console.log('📧 Verification email resent');
  } catch (error: any) {
    console.error('Resend verification error:', error.code, error.message);
    throw parseEmailAuthError(error);
  }
}

/**
 * Parse Firebase auth errors into user-friendly messages
 */
function parseEmailAuthError(error: any): EmailAuthError {
  const errorMap: Record<string, EmailAuthError> = {
    'auth/email-already-in-use': {
      code: error.code,
      message: '이미 사용 중인 이메일입니다.',
      field: 'email',
    },
    'auth/invalid-email': {
      code: error.code,
      message: '유효하지 않은 이메일 형식입니다.',
      field: 'email',
    },
    'auth/user-disabled': {
      code: error.code,
      message: '비활성화된 계정입니다. 고객센터에 문의해주세요.',
      field: 'general',
    },
    'auth/user-not-found': {
      code: error.code,
      message: '등록되지 않은 이메일입니다.',
      field: 'email',
    },
    'auth/wrong-password': {
      code: error.code,
      message: '비밀번호가 일치하지 않습니다.',
      field: 'password',
    },
    'auth/invalid-credential': {
      code: error.code,
      message: '이메일 또는 비밀번호가 일치하지 않습니다.',
      field: 'general',
    },
    'auth/weak-password': {
      code: error.code,
      message: '비밀번호는 6자 이상이어야 합니다.',
      field: 'password',
    },
    'auth/too-many-requests': {
      code: error.code,
      message: '잠시 후 다시 시도해주세요.',
      field: 'general',
    },
    'auth/network-request-failed': {
      code: error.code,
      message: '네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.',
      field: 'general',
    },
  };
  
  return errorMap[error.code] || {
    code: error.code,
    message: error.message || '알 수 없는 오류가 발생했습니다.',
    field: 'general',
  };
}

// ============================================================================
// Export
// ============================================================================

export default {
  // Social auth
  signInWithSocial,
  signInWithSocialPopup,
  signInWithSocialRedirect,
  handleSocialRedirectResult,
  isInAppBrowser,
  PROVIDER_DISPLAY,
  getEnabledProviders,
  // Email/password auth
  signUpWithEmail,
  signInWithEmail,
  resetPassword,
  resendVerificationEmail,
};
